import type { PayloadAction } from "@reduxjs/toolkit";

/**
 * Shared parts of BusinessSlice — plan §5.2. **7 of 16 reducers and the WHOLE state are shared**,
 * which makes this one of the most convergent slices on the branch: both apps agree on all eight
 * `initialState` fields, their defaults, the slice `name` (`"business"`) and the store key
 * (`businessStore`).
 *
 * What actually diverges, and why:
 * - **`getBusinessDataObjSuccess`** — web scrubs four `billTemplate` fields when running inside
 *   the Android WebView wrapper (`isAndroid()`), on a `structuredClone` of the payload; mobile
 *   assigns the payload straight through. Real behaviour, per platform.
 * - **`getBusinessDataObj`** — identical bodies, opposite ARITY: web `(state, action)`, mobile
 *   `state`. Type-level, not runtime (ledger hazard 14), and load-bearing — web dispatches it
 *   with a payload, so unifying on mobile's 1-arity form would stop web COMPILING. Kept per
 *   platform for exactly that reason; `sharedReducers` holds byte-identical bodies only.
 * - web's `extraReducers` (`userLogout` / `supportLogout`) — mobile declares none.
 * - five web-only reducers and two mobile-only ones, listed in each platform file.
 *
 * 🔴 `businessData` / `userData` are spelled `{}` on web and `Record<string, any>` on mobile.
 * They are `any` HERE and RE-DECLARED with each app's own spelling in the platform file —
 * **not unified.** Unifying them on `Record<string, any>` was tried first and `--verify` flagged
 * web's declared surface as changed — correctly: `{}` has no index signature, so it is the
 * stricter spelling, and widening it is a typecheck change the uplift is not making. Same call as
 * `ItemsSlice`'s `allItemsList: []` and hazard 10's `any[]` — preserve the app's looseness rather
 * than tidy it, and let `--verify` be the arbiter.
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger.
 */
export interface SharedBusinessState {
  isBusinessDataFetching: boolean,
  /** Re-declared per platform — web `{}`, mobile `Record<string, any>`. See above. */
  businessData: any,
  isUserDataFetching: boolean,
  /** Re-declared per platform — web `{}`, mobile `Record<string, any>`. See above. */
  userData: any,
  permittedLicences: [],
  isOffline: boolean,
  offlineTime: string,
  assignedLicence: string
}

export const sharedInitialState: SharedBusinessState = {
  isBusinessDataFetching: false,
  isUserDataFetching: false,
  businessData: {},
  userData: {},
  permittedLicences: [],
  isOffline: false,
  offlineTime: "",
  assignedLicence: ""
};

/** Identical in both apps. */
export const sharedReducers = {
  getBusinessDataObjFailure: (state: SharedBusinessState) => {
    state.isBusinessDataFetching = false;
  },
  getUserDataObj: (state: SharedBusinessState, action: PayloadAction<any>) => {
    state.isUserDataFetching = true;
  },
  /**
   * ⚠️ Reads `action.payload.assignedLicence` but assigns `action.payload.acquiredLicence` —
   * two DIFFERENT keys. Both apps do this identically, so it is not a divergence and not ours
   * to fix here; flagged in the ledger as a cleanup candidate.
   */
  getUserDataObjSuccess: (state: SharedBusinessState, action: PayloadAction<any>) => {
    state.isUserDataFetching = false;
    state.userData = action.payload;
    // To check for offline
    if (action.payload.assignedLicence) {
      state.assignedLicence = action.payload.acquiredLicence;
    }
  },
  getUserDataObjFailure: (state: SharedBusinessState) => {
    state.isUserDataFetching = false;
  },
  updateUserLicence: (state: SharedBusinessState, actions: PayloadAction<any>) => {//this will update the exisiting user assigned license

  },
  getPermittedLicences: (state: SharedBusinessState, action: PayloadAction<any>) => {
  },
  setIsOffline: (state: SharedBusinessState, action: PayloadAction<any>) => {
    state.isOffline = action.payload.isOffline;
    state.offlineTime = action.payload.offlineTime;
  },
};
